/**
 * View models for the official-weather-warnings card and list, shared by both
 * apps (`cross-app-features`: core owns the decisions and the strings, each app
 * owns the pixels).
 *
 * What crosses the seam: strings, semantic tones and raw epoch times. What does
 * not: colours (each theme maps a `WarningTone`), icon names (each app maps a
 * `WarningType`), and date formatting — the caller passes `formatTime`, so the
 * station's timezone and the 12/24-hour preference stay the app's business and
 * this module keeps no react-intl, store or clock dependency (`intl` and `now`
 * are parameters, as in `utils/Sun`).
 *
 * Imports `./model`, never the utils barrel (`importCycles.test.ts`).
 */
import {messages} from '@smartmixin/i18n';
import type {MessageDescriptor} from 'react-intl';

import type {
  WarningSeverity,
  WarningType,
  WeatherWarning,
  WeatherWarningListResponse,
} from '../../api/types';
import {
  HazardGroup,
  HazardSlice,
  currentWarnings,
  groupByHazard,
  severityRank,
} from './model';

/** Semantic colour role for a severity; each app's theme maps it to colours. */
export type WarningTone =
  | 'warningMinor'
  | 'warningModerate'
  | 'warningSevere'
  | 'warningExtreme'
  | 'warningUnknown';

const TONES: Record<WarningSeverity, WarningTone> = {
  minor: 'warningMinor',
  moderate: 'warningModerate',
  severe: 'warningSevere',
  extreme: 'warningExtreme',
  unknown: 'warningUnknown',
};

export const severityTone = (severity: WarningSeverity): WarningTone =>
  TONES[severity] ?? 'warningUnknown';

/** The hazard's name, one per backend `WarningType`. */
export const HAZARD_MESSAGES: Record<WarningType, MessageDescriptor> = {
  wind: messages.weatherWarnings.hazardWind,
  rain: messages.weatherWarnings.hazardRain,
  thunderstorm: messages.weatherWarnings.hazardThunderstorm,
  flood: messages.weatherWarnings.hazardFlood,
  snow_ice: messages.weatherWarnings.hazardSnowIce,
  avalanche: messages.weatherWarnings.hazardAvalanche,
  coastal_event: messages.weatherWarnings.hazardCoastalEvent,
  fog: messages.weatherWarnings.hazardFog,
  high_temperature: messages.weatherWarnings.hazardHighTemperature,
  low_temperature: messages.weatherWarnings.hazardLowTemperature,
  forest_fire: messages.weatherWarnings.hazardForestFire,
  uv: messages.weatherWarnings.hazardUv,
  air_quality: messages.weatherWarnings.hazardAirQuality,
  other: messages.weatherWarnings.hazardOther,
};

/**
 * The severity as a word, so colour is never the only cue (WCAG 1.4.1).
 * `unknown` has none: the status lines then fall back to "Now" / "From …" /
 * "Until …" rather than printing "Unknown".
 */
export const SEVERITY_MESSAGES: Record<
  WarningSeverity,
  MessageDescriptor | null
> = {
  minor: messages.weatherWarnings.severityMinor,
  moderate: messages.weatherWarnings.severityModerate,
  severe: messages.weatherWarnings.severitySevere,
  extreme: messages.weatherWarnings.severityExtreme,
  unknown: null,
};

/** The subset of `IntlShape` this needs. */
export type WarningsIntl = {
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, string | number>,
  ) => string;
};

export type WarningsViewParams = {
  intl: WarningsIntl;
  /**
   * Formats an epoch-seconds instant for a status line, in the place's
   * timezone and the user's clock preference, e.g. "tomorrow at 12:00 AM".
   */
  formatTime: (epochSeconds: number) => string;
};

const severityWord = (
  severity: WarningSeverity,
  intl: WarningsIntl,
): string | null => {
  const descriptor = SEVERITY_MESSAGES[severity] ?? null;
  return descriptor ? intl.formatMessage(descriptor) : null;
};

/** Worst level first, then earliest start: the slice a summary should name. */
const worstSlice = (slices: readonly HazardSlice[]): HazardSlice =>
  slices.reduce((best, s) => {
    const diff = severityRank(s.severity) - severityRank(best.severity);
    return diff > 0 || (diff === 0 && s.start < best.start) ? s : best;
  });

/**
 * When the worst level in force now ends: the LATEST end among the active
 * slices at that level (two overlapping severe slices are one severe spell).
 * Shared by the row and the band so they never quote different times.
 */
const activeUntil = (group: HazardGroup): number => {
  const active = group.slices.filter((s) => s.active);
  const rank = severityRank(worstSlice(active).severity);
  return Math.max(
    ...active
      .filter((s) => severityRank(s.severity) === rank)
      .map((s) => s.end),
  );
};

/**
 * The status of one hazard, as one or two segments the app joins with " · "
 * (and the spoken label with "then"):
 *
 * - in force now and getting worse: "Severe now", "Extreme from 12:00"
 * - in force now, not getting worse: "Moderate until 23:59" — the end of the
 *   worst slice in force, not of the whole group
 * - not started: "Minor from 14:00", plus "Severe from 17:00" if it escalates
 */
const statusSegments = (
  group: HazardGroup,
  {intl, formatTime}: WarningsViewParams,
): string[] => {
  const activeSlices = group.slices.filter((s) => s.active);
  const upcomingSlices = group.slices.filter((s) => !s.active);

  let first: HazardSlice;
  let firstText: (word: string | null) => string;
  if (activeSlices.length > 0) {
    first = worstSlice(activeSlices);
    const rank = severityRank(first.severity);
    const end = activeUntil(group);
    const escalates = upcomingSlices.some(
      (s) => severityRank(s.severity) > rank,
    );
    firstText = (word) => {
      if (escalates) {
        return word
          ? intl.formatMessage(messages.weatherWarnings.severityNow, {
              severity: word,
            })
          : intl.formatMessage(messages.common.now);
      }
      const time = formatTime(end);
      return word
        ? intl.formatMessage(messages.weatherWarnings.severityUntil, {
            severity: word,
            time,
          })
        : intl.formatMessage(messages.calendar.untilTime, {time});
    };
  } else {
    // Everything is upcoming: start from the earliest slice (the worst of
    // those sharing that start).
    const earliest = Math.min(...group.slices.map((s) => s.start));
    first = worstSlice(group.slices.filter((s) => s.start === earliest));
    firstText = (word) => {
      const time = formatTime(first.start);
      return word
        ? intl.formatMessage(messages.weatherWarnings.severityFrom, {
            severity: word,
            time,
          })
        : intl.formatMessage(messages.weatherWarnings.fromTime, {time});
    };
  }

  const segments = [firstText(severityWord(first.severity, intl))];
  const later = upcomingSlices.filter(
    (s) => severityRank(s.severity) > severityRank(first.severity),
  );
  if (later.length > 0) {
    const peak = worstSlice(later);
    // Ranked above `first`, so never `unknown`: it always has a word.
    segments.push(
      intl.formatMessage(messages.weatherWarnings.severityFrom, {
        severity: severityWord(peak.severity, intl) as string,
        time: formatTime(peak.start),
      }),
    );
  }
  return segments;
};

type GroupBase = {
  key: string;
  hazard: WarningType;
  /** Tone of the worst level the hazard reaches. */
  tone: WarningTone;
  hazardName: string;
  /** The worst level as a word; null when unknown. */
  severity: string | null;
  /** In force now (else it only starts later). */
  active: boolean;
  /** One or two status segments, e.g. ["Severe now", "Extreme from 12:00"]. */
  line: string[];
  areaName: string | null;
};

const groupBase = (
  group: HazardGroup,
  params: WarningsViewParams,
): GroupBase => {
  const peak = worstSlice(group.slices);
  return {
    key: group.key,
    hazard: group.hazard,
    tone: severityTone(group.severity),
    hazardName: params.intl.formatMessage(HAZARD_MESSAGES[group.hazard]),
    severity: severityWord(group.severity, params.intl),
    active: group.active,
    line: statusSegments(group, params),
    areaName: peak.warning.area_name || null,
  };
};

export type WarningSummaryRow = GroupBase;

export type WarningSummaryBand = {
  tone: WarningTone;
  severity: string | null;
  hazard: string;
  /** "Until 23:59" when the worst is in force now, else "From 12:00". */
  when: string;
};

export type WarningSummaryViewModel =
  | {kind: 'none'}
  | {
      kind: 'warnings';
      /**
       * The card's lead: the worst level COMING (not merely the worst now),
       * its hazard and when — "Extreme · Rain · From tomorrow at 12:00 AM".
       */
      band: WarningSummaryBand;
      /** One per hazard, worst first; the card shows the first few. */
      rows: WarningSummaryRow[];
      hazardCount: number;
      /** API rows still current (several can make up one hazard). */
      warningCount: number;
      /**
       * The whole card read as one item: count, then one sentence per hazard.
       * For `accessibilityLabel` on mobile and `aria-label` on the webapp.
       */
      accessibleName: string;
    };

/**
 * The dashboard card. `warnings` may include expired rows (the point response
 * is cached for an hour); `now` is epoch seconds.
 */
export const summaryViewModel = (
  warnings: readonly WeatherWarning[],
  now: number,
  params: WarningsViewParams,
): WarningSummaryViewModel => {
  const groups = groupByHazard(warnings, now);
  if (groups.length === 0) {
    return {kind: 'none'};
  }
  const {intl, formatTime} = params;
  const rows = groups.map((g) => groupBase(g, params));

  // Groups are sorted worst first, so the first holds the worst level coming.
  const lead = groups[0];
  const peak = worstSlice(lead.slices);
  const band: WarningSummaryBand = {
    tone: rows[0].tone,
    severity: rows[0].severity,
    hazard: rows[0].hazardName,
    when: peak.active
      ? intl.formatMessage(messages.calendar.untilTime, {
          time: formatTime(activeUntil(lead)),
        })
      : intl.formatMessage(messages.weatherWarnings.fromTime, {
          time: formatTime(peak.start),
        }),
  };

  const sentences = rows.map((row) =>
    row.line.length > 1
      ? intl.formatMessage(messages.weatherWarnings.spokenHazardTwo, {
          hazard: row.hazardName,
          first: row.line[0],
          second: row.line[1],
        })
      : intl.formatMessage(messages.weatherWarnings.spokenHazardOne, {
          hazard: row.hazardName,
          status: row.line[0],
        }),
  );

  return {
    kind: 'warnings',
    band,
    rows,
    hazardCount: groups.length,
    warningCount: currentWarnings(warnings, now).length,
    accessibleName: intl.formatMessage(messages.weatherWarnings.spokenLabel, {
      count: groups.length,
      hazards: sentences.join(' '),
    }),
  };
};

export type WarningListSlice = {
  id: number;
  tone: WarningTone;
  severity: string | null;
  active: boolean;
  /** Epoch seconds; the app formats them for its lane and period rows. */
  start: number;
  end: number;
};

export type WarningListGroup = GroupBase & {
  /** The worst slice's own headline, from the agency. */
  headline: string;
  /** "Source: Meteo Alarm"; null when the row names none. */
  source: string | null;
  slices: WarningListSlice[];
};

export type WarningListSection = {
  id: 'active' | 'upcoming';
  title: string;
  groups: WarningListGroup[];
};

export type WarningListViewModel = {
  /** "Last update: 21:26", from `computed_at`; null when it is missing. */
  lastUpdate: string | null;
  /** "Active now" then "Upcoming"; an empty section is left out. */
  sections: WarningListSection[];
};

/** The warnings list: hazard groups under "Active now" / "Upcoming". */
export const listViewModel = (
  response: WeatherWarningListResponse,
  now: number,
  params: WarningsViewParams,
): WarningListViewModel => {
  const {intl, formatTime} = params;
  const computedAt = response?.computed_at;
  const groups = groupByHazard(response?.warnings ?? [], now).map(
    (group): WarningListGroup => {
      const peak = worstSlice(group.slices);
      const source = peak.warning.source;
      return {
        ...groupBase(group, params),
        headline: peak.warning.headline,
        source: source
          ? intl.formatMessage(messages.weatherWarnings.source, {source})
          : null,
        slices: group.slices.map((s) => ({
          id: s.warning.id,
          tone: severityTone(s.severity),
          severity: severityWord(s.severity, intl),
          active: s.active,
          start: s.start,
          end: s.end,
        })),
      };
    },
  );

  const sections: WarningListSection[] = [];
  const active = groups.filter((g) => g.active);
  const upcoming = groups.filter((g) => !g.active);
  if (active.length > 0) {
    sections.push({
      id: 'active',
      title: intl.formatMessage(messages.weatherWarnings.activeNow),
      groups: active,
    });
  }
  if (upcoming.length > 0) {
    sections.push({
      id: 'upcoming',
      title: intl.formatMessage(messages.weatherWarnings.upcoming),
      groups: upcoming,
    });
  }

  return {
    lastUpdate:
      typeof computedAt === 'number' && Number.isFinite(computedAt)
        ? intl.formatMessage(messages.weatherWarnings.lastUpdate, {
            time: formatTime(computedAt),
          })
        : null,
    sections,
  };
};
